import { useEffect, useRef, useState } from 'react'
import { Html5Qrcode } from 'html5-qrcode'
import { ModalShell, m } from './ui/ModalShell'

interface Props {
  onScan: (token: string) => void
  onCancel: () => void
}

// S8 T39: helper safe-stop. html5-qrcode tira EXCEPCION SINCRONA cuando
// se llama stop() sobre un scanner ya detenido (no rechaza promise). El
// .catch() del .stop() original NO atrapa eso -> uncaught -> React unmount
// crash -> pantalla blanca tras escaneo exitoso.
async function safeStop(scanner: Html5Qrcode | null): Promise<void> {
  if (!scanner) return
  try {
    const result = scanner.stop()
    if (result && typeof (result as Promise<void>).catch === 'function') {
      await (result as Promise<void>).catch(() => {})
    }
  } catch {
    // Scanner ya no estaba running -> ignorar
  }
}

export default function QRScanner({ onScan, onCancel }: Props) {
  const scannerRef = useRef<Html5Qrcode | null>(null)
  const onScanRef = useRef(onScan)
  const containerId = 'qr-scanner-container'
  // QA 5-jul: fallback manual. Si la tablet no tiene cámara (o falla), el
  // auxiliar puede TECLEAR/pegar el código del QR. Sin esto el flujo entero
  // (alergias→oferta→cocina) quedaba bloqueado por hardware.
  const [manual, setManual] = useState('')

  async function usarManual() {
    const codigo = manual.trim()
    if (!codigo) return
    await safeStop(scannerRef.current)
    onScanRef.current(codigo)
  }

  // S8 T39: actualizar ref sin re-disparar el useEffect del scanner.
  // Antes useEffect tenia [onScan] como dep -> si el padre re-rendereaba
  // con nueva referencia de la prop, se destruia el scanner y se recreaba
  // = condicion de carrera adicional.
  useEffect(() => {
    onScanRef.current = onScan
  }, [onScan])

  useEffect(() => {
    const scanner = new Html5Qrcode(containerId)
    scannerRef.current = scanner

    scanner
      .start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decodedText) => {
          await safeStop(scanner)
          onScanRef.current(decodedText)
        },
        () => {},
      )
      .catch((err) => console.error('Error iniciando scanner:', err))

    return () => {
      // Cleanup async-aware: si el callback de decode YA llamo stop, esta
      // segunda llamada es la que tiraba "Cannot stop". safeStop la captura.
      safeStop(scanner)
    }
  }, [])

  return (
    <ModalShell title="Apunta a un código QR" onClose={onCancel} maxWidth={560}>
      <div id={containerId} className="w-full rounded overflow-hidden bg-black" />

      {/* Fallback manual (sin cámara) */}
      <div className="mt-4 rounded-xl p-3" style={m.subCard}>
        <p className="text-xs mb-2" style={m.labelSuave}>
          ¿Sin cámara? Escribe o pega el código del QR:
        </p>
        <div className="flex gap-2">
          <input
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && usarManual()}
            placeholder="Código del QR"
            className="flex-1 rounded-lg px-3 py-2 text-sm"
            style={m.input}
          />
          <button
            onClick={usarManual}
            disabled={!manual.trim()}
            className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
            style={m.btnPrimary}
          >
            Usar código
          </button>
        </div>
      </div>

      <div className="mt-4 flex justify-center">
        <button
          onClick={onCancel}
          className="px-6 py-2 rounded-lg text-sm font-semibold"
          style={m.btnGhost}
        >
          Cancelar
        </button>
      </div>
    </ModalShell>
  )
}
